import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loader2 } from "lucide-react";
import { initials } from "@/lib/format";
import { toast } from "sonner";

export default function ProfilePage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/profile/me").then(({ data }) => setProfile(data.profile || {})).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      if (user.role === "instructor") {
        await api.put("/profile/instructor", {
          name: profile.name, bio: profile.bio, experience_years: Number(profile.experience_years),
          hourly_rate: Number(profile.hourly_rate), location: profile.location, vehicle: profile.vehicle, languages: profile.languages,
        });
      } else {
        await api.put("/profile/student", { phone: profile.phone, license_stage: profile.license_stage });
      }
      toast.success("Profile saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const set = (k) => (e) => setProfile({ ...profile, [k]: e.target.value });

  if (loading) return <div className="min-h-screen bg-background"><Navbar /><div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-arctic-primary" /></div></div>;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-4">
          <Avatar className="w-16 h-16 border border-border">
            <AvatarImage src={user.picture} alt={user.name} />
            <AvatarFallback className="bg-arctic-primary text-white text-lg">{initials(user.name)}</AvatarFallback>
          </Avatar>
          <div>
            <h1 className="font-heading font-extrabold text-2xl text-arctic-secondary tracking-tight" data-testid="profile-title">{user.name}</h1>
            <p className="text-muted-foreground text-sm">{user.email} · <span className="capitalize">{user.role}</span></p>
          </div>
        </div>

        <Card className="p-6 rounded-2xl border-border mt-8 space-y-4">
          {user.role === "instructor" ? (
            <>
              <div className="space-y-2"><Label>Display name</Label><Input value={profile.name || ""} onChange={set("name")} data-testid="profile-name" /></div>
              <div className="space-y-2"><Label>Bio</Label><Textarea value={profile.bio || ""} onChange={set("bio")} data-testid="profile-bio" rows={3} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Experience (years)</Label><Input type="number" value={profile.experience_years ?? ""} onChange={set("experience_years")} data-testid="profile-experience" /></div>
                <div className="space-y-2"><Label>Rate per lesson ($)</Label><Input type="number" value={profile.hourly_rate ?? ""} onChange={set("hourly_rate")} data-testid="profile-rate" /></div>
              </div>
              <div className="space-y-2"><Label>Location / area</Label><Input value={profile.location || ""} onChange={set("location")} data-testid="profile-location" /></div>
              <div className="space-y-2"><Label>Vehicle</Label><Input value={profile.vehicle || ""} onChange={set("vehicle")} data-testid="profile-vehicle" /></div>
              <div className="space-y-2"><Label>Languages</Label><Input value={profile.languages || ""} onChange={set("languages")} data-testid="profile-languages" /></div>
            </>
          ) : (
            <>
              <div className="space-y-2"><Label>Phone</Label><Input value={profile.phone || ""} onChange={set("phone")} data-testid="profile-phone" /></div>
              <div className="space-y-2"><Label>License stage</Label><Input value={profile.license_stage || ""} onChange={set("license_stage")} data-testid="profile-license" placeholder="Beginner / Intermediate / Test-ready" /></div>
            </>
          )}
          <Button onClick={save} disabled={saving} data-testid="save-profile" className="rounded-full bg-arctic-primary hover:bg-arctic-primary/90">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
          </Button>
        </Card>
      </main>
    </div>
  );
}
